import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import i18n from 'i18n-literally'
import { useConnect } from 'redux-bundler-hook'

import { CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'

import ms from 'milliseconds'

import {
  Allowed,
  available,
  useAllowed,
  useFlags,
} from '~/src/Flags'
import { INVENTORY_TYPES } from '~/src/Inventory/constants'
import { getInventoryPermits } from '~/src/Inventory/utils'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_ARRAY, lazyWithRetries, useIsLoaded } from '~/src/Lib/Utils'
import Tab, { Tabs } from '~/src/UI/Shared/Tab'
import { CultivationTheme } from '~/src/UI/Theme'

import PhaseButton from '../PhaseButton'
import ActiveHarvestChooser from './ActiveHarvestChooser'
import MoreMenu from './MoreMenu'
import HarvestSummary from './Summary'
import HarvestTimeline from './Timeline'
import useHarvestEntity from './useHarvestEntity'

const HarvestSchedule = lazyWithRetries(() => import('./Schedule'))
const InventoryWrapper = lazyWithRetries(() => import('./Adapters/InventoryWrapper'))
const YieldWrapper = lazyWithRetries(() => import('./Adapters/YieldWrapper'))
const Location = lazyWithRetries(() => import('./Location'))
const RoomDashboard = lazyWithRetries(() => import('~/src/Room/Routes/Dashboard'))
const HarvestAnalytics = lazyWithRetries(() => import('../Analytics'))

const displayName = 'Harvest/Detail'
const logger = createLogger(displayName)

const TABS = [
  {
    label: i18n`Environment`,
    component: RoomDashboard,
    subpath: 'growlog',
    actions: ['view_harvest'],
  },
  {
    label: i18n`Schedule`,
    component: HarvestSchedule,
    subpath: 'schedule',
    actions: ['view_recipe'],
  },
  {
    label: i18n`Cultivars/Location`,
    component: Location,
    subpath: 'cultivars',
    actions: ['view_harvest'],
  },
  {
    label: i18n`Inventory`,
    component: InventoryWrapper,
    subpath: 'inventory',
    actions: ['view_harvest', getInventoryPermits],
  },
  {
    label: i18n`Yield`,
    component: YieldWrapper,
    subpath: 'yield',
    actions: ['view_harvest'],
    features: ['METRC'],
    negate: ['features'],
  },
  {
    label: i18n`Analytics`,
    component: HarvestAnalytics,
    subpath: 'analytics',
    actions: ['view_harvest'],
  },
]

const TABS_DISABLED_CHECKS = {
  inventory: ({ harvest, features }) => !available(features, ['METRC']) && !harvest.harvestedCount,
}

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '100%',
    gridTemplateRows: 'auto 1fr',
    overflow: 'hidden',
    '--scroll-bg': theme.palette.batch.active,
    '--scroll-fg': theme.palette.batch.disabled,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    '& [class*=MuiTabs] [class*=Mui-disabled] span': {
      color: theme.palette.text.secondary,
    },
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& >:first-child': {
      marginRight: 'auto',
    },
    '& > a': {
      minWidth: 'max-content',
    },
  },
  floatContainer: {
    marginBottom: theme.spacing(1),
    '& >:last-child': {
      float: 'right',
    },
    '& >:first-child': {
      float: 'left',
      marginBottom: theme.spacing(1),
    },
  },
  tabPanel: {
    display: 'contents',
  },
}), { name: displayName })

const TabPanel = ({ classes, value, index, Panel, ...props }) => (
  <div
    className={classes.tabPanel}
    role="tabpanel"
    hidden={value !== index}
  >
    {value === index && Panel && <Panel {...props} />}
  </div>
)

const HarvestDetail = ({ harvest }) => {
  const {
    phasesByHarvest,
    routeInfo: {
      params,
      url,
    },
    doUpdateUrl,
  } = useConnect(
    'selectPhasesByHarvest',
    'selectRouteInfo',
    'doUpdateUrl',
  )
  const [canViewHarvest] = useAllowed({ actions: 'view_harvest' })
  const [canChangeHarvest] = useAllowed({ actions: 'change_harvest' })
  const features = useFlags()

  useEffect(() => {
    if (url === `/harvests/${params.id}` && canViewHarvest) {
      doUpdateUrl(`${url}/${TABS[0].subpath}`, { replace: true })
    }
  }, [canViewHarvest, doUpdateUrl, params.id, url])

  const tabValue = TABS.findIndex(tab => url.endsWith(tab.subpath)) ?? 0

  const harvestPhases = useMemo(() => (
    harvest.id in phasesByHarvest && Array.isArray(phasesByHarvest[harvest.id])
      ? phasesByHarvest[harvest.id].sort((a, b) => a.sequence - b.sequence)
      : EMPTY_ARRAY
  ), [harvest, phasesByHarvest])

  const floatingContainerRef = useRef(null)

  const classes = useStyles()

  return (
    <CultivationTheme>
      <div id="harvest-detail-root" className={classes.root}>
        <ActiveHarvestChooser currentHarvest={harvest} />

        <div className={classes.header}>
          <div className={classes.summaryRow}>
            <HarvestSummary harvest={harvest} harvestPhases={harvestPhases} />
            <PhaseButton harvest={harvest} />
            <MoreMenu harvest={harvest} />
          </div>

          <HarvestTimeline harvest={harvest} harvestPhases={harvestPhases} />

          <div className={classes.floatContainer}>
            <Tabs value={tabValue}>
              {TABS.map(({ label, subpath, ...rest }) => (
                <Allowed {...rest} key={label}>
                  <Tab
                    key={label}
                    component="a"
                    disabled={subpath in TABS_DISABLED_CHECKS && TABS_DISABLED_CHECKS[subpath]({ harvest, features })}
                    href={`/harvests/${harvest.id}/${subpath}`}
                    label={label}
                  />
                </Allowed>
              ))}
            </Tabs>
            <div ref={floatingContainerRef} />
          </div>
        </div>
        {TABS.map(({ label, component, ...rest }, index) => (
          <Suspense fallback={<div className="center-all"><CircularProgress color="primary" /></div>}>
            <Allowed {...rest} key={label}>
              <TabPanel
                canEdit={canChangeHarvest}
                classes={classes}
                floatingContainerRef={floatingContainerRef}
                harvest={harvest}
                harvestPhases={harvestPhases}
                index={index}
                Panel={component}
                scrollElementId="harvest-detail-root"
                value={tabValue}
              />
            </Allowed>
          </Suspense>
        ))}
      </div>
    </CultivationTheme>
  )
}

const HarvestDetailLoader = () => {
  const {
    routeInfo,
    doHarvestInventorySetParams,
    doUpdateUrl,
  } = useConnect(
    'selectRouteInfo',
    'doHarvestInventorySetParams',
    'doUpdateUrl',
  )
  const { params, pattern } = routeInfo
  const harvest = useHarvestEntity(params.id)

  useEffect(() => {
    if (harvest?.id) {
      const inventoryParams = {
        harvests: [harvest.id],
        hideFinished: true,
        types: INVENTORY_TYPES.ALL,
      }
      doHarvestInventorySetParams(inventoryParams)
      logger.debug('inventory params set', inventoryParams)
    }
  }, [doHarvestInventorySetParams, harvest?.id])

  const loaded = useIsLoaded(!harvest, ms.seconds(5))
  if (pattern.startsWith('/harvests/:id') && loaded && !harvest) {
    // Redirect to the harvest list on timeout
    doUpdateUrl('/harvests')
  }

  return harvest ? (
    <HarvestDetail harvest={harvest} />
  ) : (
    <div className="center-all">
      <CircularProgress color="secondary" size={100} />
    </div>
  )
}

const Memoized = React.memo(HarvestDetailLoader)
Memoized.$preload = TABS

export default Memoized
